import type { SimulationState, ThemeData, Project, Product, PlatformDefinition } from '../types';
import type { GameEvent } from '../events/GameEvents';
import type { SeededRandom } from '../rng/SeededRandom';

/** Calculate dynamic installed base based on platform lifecycle. */
function getInstalledBase(p: PlatformDefinition, currentYear: number): number {
  const peak = p.peakInstalledBase ?? p.installedBase ?? 50000;
  const yearsActive = currentYear - p.releasedYear;

  if (yearsActive < 0) return 0; // Not released yet

  // Growth phase: ramps up to peak over growthYears
  const growthYears = p.growthYears ?? 3;
  const growthRatio = Math.min(1, yearsActive / growthYears);
  let base = peak * growthRatio;

  // Decay phase: after retirement, drops off over 3 years
  if (p.retiredYear && currentYear >= p.retiredYear) {
    const yearsRetired = currentYear - p.retiredYear;
    const decayFactor = Math.max(0, 1 - yearsRetired * 0.3);
    base *= decayFactor;
  }

  return Math.round(base);
}

/** Exported for use in Start Project dialog. */
export { getInstalledBase };

function generateReviewScores(
  quality: number,
  category: string,
  subcategory: string,
  themeData: ThemeData,
  isHardMode: boolean,
  rng: SeededRandom,
  currentYear: number = 1,
  marketTrends: Record<string, number> = {},
  recentProducts: Product[] = [],
): number[] {
  // Base score from quality (0-100 → 1-9.5 scale — 10/10 should be exceptional)
  let baseScore = 1 + (quality / 100) * 8.5;

  // Category combo affinity — use dynamic market trends
  const affinityKey = `${category}+${subcategory}`;
  const affinity = marketTrends[affinityKey] ?? themeData.entities.categoryComboAffinity[affinityKey] ?? 1.0;
  baseScore *= affinity;

  // Market expectations: reviewers get harder to impress over time (1.5% per year)
  baseScore *= Math.max(0.6, 1 - currentYear * 0.015);

  // Repetition fatigue: same genre+theme combo gets stale
  // Count recent releases (last 3 years) with same combo
  if (recentProducts) {
    const recentSame = recentProducts.filter(
      (p) => p.category === category && p.subcategory === subcategory &&
        currentYear - p.releaseYear <= 3,
    ).length;
    if (recentSame > 0) {
      baseScore *= Math.max(0.6, 1 - recentSame * 0.08); // -8% per recent same-combo game
    }
  }

  const variance = isHardMode
    ? themeData.balance.hardMode.reviewVariance
    : themeData.balance.reviewVariance;

  const scores: number[] = [];
  for (let i = 0; i < 4; i++) {
    const score = baseScore + rng.nextFloat(-variance, variance);
    scores.push(Math.max(1, Math.min(10, Math.round(score * 10) / 10)));
  }

  return scores;
}

function calculatePeakSales(
  averageReview: number,
  fans: number,
  platformInstalledBase: number,
  marketingMultiplier: number,
  sequelBonus: number,
  competitorPenalty: number,
  uncontestedBonus: number,
  projectSize: string = 'small',
): number {
  const reviewMultiplier = Math.pow(averageReview / 5, 2); // Exponential scaling
  // Logarithmic fan scaling to prevent runaway compounding
  const fanMultiplier = 1 + Math.log10(Math.max(fans, 1) + 1) * 0.3;
  const platformMultiplier = Math.log10(Math.max(platformInstalledBase, 1000)) / 3;

  // Bigger projects have bigger market potential
  const sizeMultiplier: Record<string, number> = { small: 1, medium: 2.5, large: 5, aaa: 10 };
  const sizeMult = sizeMultiplier[projectSize] ?? 1;

  return Math.round(
    1500 *
      reviewMultiplier *
      fanMultiplier *
      platformMultiplier *
      marketingMultiplier *
      sequelBonus *
      (1 - competitorPenalty) *
      (1 + uncontestedBonus) *
      sizeMult,
  );
}

export function releaseProduct(
  project: Project,
  state: SimulationState,
  themeData: ThemeData,
  rng: SeededRandom,
): { product: Product; events: GameEvent[] } {
  const events: GameEvent[] = [];

  // Sequel quality adjustment
  let sequelBonus = 1.0;
  if (project.sequelOf) {
    const predecessor = state.products.find((p) => p.id === project.sequelOf);
    if (predecessor && predecessor.averageReview >= 7.0) {
      sequelBonus = 1 + themeData.balance.sequelQualityBonus;
    }
    if (project.sequelNumber > themeData.balance.sequelFatigueStartsAt) {
      const fatigueCount = project.sequelNumber - themeData.balance.sequelFatigueStartsAt;
      sequelBonus -= fatigueCount * themeData.balance.sequelFatiguePerSequel;
      sequelBonus = Math.max(0.5, sequelBonus);
    }
  }

  const adjustedQuality = Math.min(100, project.quality * sequelBonus);
  const scores = generateReviewScores(
    adjustedQuality,
    project.category,
    project.subcategory,
    themeData,
    state.company.isHardMode,
    rng,
    state.company.currentYear,
    state.marketTrends,
    state.products,
  );
  const averageReview = scores.reduce((a, b) => a + b, 0) / scores.length;
  const isHallOfFame = averageReview >= themeData.balance.hallOfFameThreshold;

  // Marketing multiplier
  const strategy = themeData.entities.marketingStrategies.find(
    (s) => s.id === project.marketingStrategyId,
  );
  let marketingMultiplier = strategy?.salesMultiplier ?? 1.0;
  if (strategy) {
    const catBonus = strategy.categoryBonuses[project.category];
    const catPenalty = strategy.categoryPenalties[project.category];
    if (catBonus) marketingMultiplier *= catBonus;
    else if (catPenalty) marketingMultiplier *= catPenalty;
  }

  // Platform installed base — sum across all target platforms
  const projectPlatforms = project.platforms?.length ? project.platforms : [project.platform];
  let installedBase = 0;
  for (const pid of projectPlatforms) {
    const plat = themeData.entities.platforms.find((p) => p.id === pid);
    if (plat) installedBase += getInstalledBase(plat, state.company.currentYear);
  }
  if (installedBase === 0) installedBase = 50000;

  // Competitor impact
  let competitorPenalty = 0;
  let isUncontested = true;
  for (const comp of state.competitors) {
    for (const cp of comp.products) {
      if (cp.isActive && cp.category === project.category) {
        isUncontested = false;
        const penaltyRange = themeData.balance.contestedMarketPenalty;
        const qualityRatio = cp.quality / 100;
        competitorPenalty += penaltyRange[0]! + (penaltyRange[1]! - penaltyRange[0]!) * qualityRatio;
      }
    }
  }
  competitorPenalty = Math.min(competitorPenalty, 0.6); // Cap at 60%

  const uncontestedBonus = isUncontested ? themeData.balance.uncontestedMarketBonus : 0;

  const peakSales = calculatePeakSales(
    averageReview,
    state.company.fans,
    installedBase,
    marketingMultiplier,
    sequelBonus,
    competitorPenalty,
    uncontestedBonus,
    project.size,
  );

  // Fan growth (capped to prevent runaway)
  const baseFanGrowth = Math.min(Math.round(peakSales * 0.05), 5000);
  const fanGrowth = isHallOfFame
    ? baseFanGrowth * themeData.balance.fanGrowthHoFMultiplier
    : baseFanGrowth;
  state.company.fans += Math.round(fanGrowth);

  // Company XP from release
  const sizeMultiplier = { small: 1, medium: 2, large: 3, aaa: 5 }[project.size];
  const companyXP = Math.round(averageReview * 10 * sizeMultiplier);
  state.company.xp += companyXP;

  state.company.totalProductsReleased++;
  if (isHallOfFame) state.company.hallOfFameCount++;

  const product: Product = {
    id: project.id,
    name: project.name,
    category: project.category,
    subcategory: project.subcategory,
    platform: project.platform,
    platforms: projectPlatforms,
    qualityScore: adjustedQuality,
    reviewScores: scores,
    averageReview,
    weeklySales: [peakSales],
    totalSales: 0,
    totalRevenue: 0,
    releaseYear: state.company.currentYear,
    releaseWeek: state.company.currentWeek,
    isHallOfFame,
    sequelNumber: project.sequelNumber,
    size: project.size,
    marketingStrategyId: project.marketingStrategyId,
  };

  events.push({
    type: 'PRODUCT_RELEASED',
    productId: product.id,
    name: product.name,
  });
  events.push({
    type: 'REVIEW_SCORES_GENERATED',
    productId: product.id,
    name: product.name,
    scores,
    average: averageReview,
    isHallOfFame,
  });

  return { product, events };
}

/**
 * MarketSystem processes completed projects → releases, and manages ongoing sales.
 */
export function marketSystemUpdate(
  state: SimulationState,
  themeData: ThemeData,
  rng: SeededRandom,
): GameEvent[] {
  const events: GameEvent[] = [];

  // Check for completed projects that need releasing
  const completedProjects = state.projects.filter((p) => p.phase === 'complete');
  for (const project of completedProjects) {
    const { product, events: releaseEvents } = releaseProduct(project, state, themeData, rng);
    state.products.push(product);
    events.push(...releaseEvents);
  }

  // Remove completed projects from the active list
  state.projects = state.projects.filter((p) => p.phase !== 'complete');

  // Market trend drift — once per year (first tick of each year)
  const ticksPerWeek = themeData.balance.ticksPerDay * themeData.balance.daysPerWeek;
  const ticksPerYear = ticksPerWeek * themeData.balance.weeksPerYear;
  if (state.company.currentTick > 0 && state.company.currentTick % ticksPerYear === 0 && state.marketTrends) {
    // Each combo drifts randomly toward neutral (1.0) or away
    for (const key of Object.keys(state.marketTrends)) {
      const current = state.marketTrends[key]!;
      const drift = rng.nextFloat(-0.15, 0.15);
      // Pull slightly toward 1.0 (mean reversion) + random drift
      const meanReversion = (1.0 - current) * 0.1;
      state.marketTrends[key] = Math.max(0.5, Math.min(1.6, current + drift + meanReversion));
    }
    // Occasionally introduce a new hot trend or kill an old one
    const combos = Object.keys(state.marketTrends);
    if (combos.length > 0 && rng.nextBool(0.3)) {
      const hotCombo = rng.pick(combos);
      state.marketTrends[hotCombo] = rng.nextFloat(1.2, 1.5);
    }
    if (combos.length > 0 && rng.nextBool(0.3)) {
      const coldCombo = rng.pick(combos);
      state.marketTrends[coldCombo] = rng.nextFloat(0.6, 0.85);
    }
  }

  return events;
}
